import type { SyntheticEvent } from 'react';
import { Link } from 'react-router';
import { useSearchFilters } from '@modules/search/hook/use-search-filters';
import type { AppDisplayModeType } from '@archipro-design/aria';
import { pxToRem, useTheme } from '@archipro-design/aria';
import {
    Grid,
    useAppDisplayModeContext,
    useStyles,
    Container,
    Divider,
} from '@archipro-design/aria';
import * as S from './PaginatedJournalArticles.style';
import BottomPagination from '@modules/professional/component/shared/BottomPagination';
import type {
    PaginatedArticleTileProps,
    PaginatedArticlesProps,
} from '@modules/professional/type/company-articles';
import { getArchizeenTileVariantSize } from '@modules/professional/util/company-article-helper';
import { NoSearchResults } from '@modules/professional/component/shared/NoSearchResults';
import { SearchFilter } from '@modules/professional/component/shared/SearchFilter';
import type { Tracker } from '@archipro-website/tracker';
import { useTracker } from '@archipro-website/tracker';
import { useToggle } from 'ahooks';
import type { GetTileHotButtonsProps } from '~/modules/root/hook';
import { useMatchesHandleData, useTileHotButtons } from '~/modules/root/hook';
import type { SearchIndex } from '~/modules/search/type';
import { IN_PAGE_SEARCH_IGNORE_FILTER_KEYS } from '~/modules/professional/config/constants';
import AppliedFilters from '~/modules/search/components/search-filter-section/AppliedFilters';
import { resetFilterStates } from '~/modules/search/util/reset-filter-states';
import { Container as PageLayoutContainer } from '@modules/professional/component/page-layout/PageLayout.style';
import ArchizeenTile from '~/modules/tile/component/archizeen-tile/ArchizeenImpressionTile';
import { visitBMEditor } from '~/modules/directory/util/tile-helpers';
import { getImagesSizes } from '~/utils/image-sizes-utils';

const ArticleTiles = ({
    articles,
    appDisplayMode,
    tracker,
    disableTileLink,
    disableSaveToDesignBoard,
    isBMPreview,
    getTileTopActions,
    isProfessionalProfile,
}: {
    articles: PaginatedArticleTileProps[];
    appDisplayMode: AppDisplayModeType;
    tracker: Tracker;
    disableTileLink?: boolean;
    disableSaveToDesignBoard?: boolean;
    isBMPreview?: boolean;
    getTileTopActions: <T>({
        item,
        adminItem,
        onDeleteClick,
        editOnSameTab,
    }: GetTileHotButtonsProps<T>) => React.ReactNode;
    isProfessionalProfile: boolean;
}) => {
    const mobile = appDisplayMode.mobile;
    const renderTileAs = disableTileLink ? 'div' : Link;
    return (
        <>
            {articles.map((article, index) => {
                const {
                    link,
                    id,
                    image,
                    professionID: _professionID,
                    canEdit: _canEdit,
                    bmEditorLink: _bmEditorLink,
                    title,
                    ...rest
                } = article;
                const variant = mobile
                    ? '04'
                    : getArchizeenTileVariantSize(index).variant;

                const imageProps = {
                    src: image,
                    width: '100%',
                    height: '100%',
                    sizes: mobile
                        ? getImagesSizes({ desktop: 340, mobile: 120 })
                        : getImagesSizes({ desktop: 434, mobile: 120 }),
                    alt: rest.label,
                };

                const editOnSameTab = disableTileLink || isBMPreview;
                const linkProps = !disableTileLink
                    ? {
                          to: link,
                          prefetch: 'intent',
                          target: !isProfessionalProfile
                              ? '_parent'
                              : undefined,
                          state: {
                              hideAd: true,
                          },
                      }
                    : {};

                return (
                    <div key={id}>
                        <ArchizeenTile
                            articleId={id}
                            as={renderTileAs}
                            {...linkProps}
                            image={imageProps}
                            {...rest}
                            title={{
                                children: title,
                                maximumLines: mobile ? 3 : undefined,
                            }}
                            variant={variant}
                            size={
                                !mobile || index % 2 === 0 ? 'medium' : 'small'
                            }
                            favouritePosition={mobile ? 'card' : 'image'}
                            hideFavourite={disableSaveToDesignBoard}
                            onClick={(e: SyntheticEvent) => {
                                if (isBMPreview) {
                                    // prevent default to avoid navigating to public page, onTileClick should handle the navigation in BM
                                    e.preventDefault();
                                }
                                tracker.log('ProfessionalArticleClick', {
                                    url: new URL(window.location.href),
                                    targetTracker: 'archiproTracker',
                                });

                                if (isBMPreview || disableTileLink) {
                                    visitBMEditor(article, true);
                                }
                            }}
                            impression={{
                                professionalID: article.professionID ?? 0,
                                itemID: id,
                                type: 'LibraryItem_Directory',
                            }}
                            topActions={getTileTopActions({
                                item: article,
                                editOnSameTab,
                            })}
                        />
                    </div>
                );
            })}
        </>
    );
};

const PaginatedJournalArticles: React.FC<PaginatedArticlesProps> = ({
    articles,
    totalCount,
    totalPage,
    currentPage,
    pageSize,
    hasPreviousPage,
    hasNextPage,
    filterStates,
    disableTileLink,
    disableSaveToDesignBoard,
    disableSort,
}) => {
    const hasPagination = totalPage !== 1;
    const appDisplayMode = useAppDisplayModeContext();
    const { mobile, desktop } = appDisplayMode;
    const isProfessionalProfile = useMatchesHandleData(
        'isProfessionalProfile',
        false,
    );

    const { css } = useStyles({ hasPagination, totalCount });
    const { siteVariables } = useTheme();

    const isBMPreview = useMatchesHandleData('isBMPreview', false);

    const [filterDrawerOpen, { toggle: toggleFilterDrawer }] =
        useToggle(!disableSort);

    const tracker = useTracker();

    const ignoreFilterKeys = IN_PAGE_SEARCH_IGNORE_FILTER_KEYS;
    const category = useMatchesHandleData<SearchIndex>('category', 'project');

    const searchFilters = useSearchFilters({
        category,
        initialStates: filterStates,
        isCategoryPage: false,
        ignoreFilterKeys,
    });

    const articleIDs = articles.map((article) => article.id);

    const { getTileTopActions, hotButtonModal } = useTileHotButtons({
        itemType: 'article',
        itemIDs: articleIDs,
    });
    return (
        <Container>
            <div className={css(S.ArticleSection)}>
                <div className={css(PageLayoutContainer)}>
                    {desktop && (
                        <Divider
                            size={1}
                            variables={{
                                dividerPadding: 0,
                                dividerPrimaryColor:
                                    siteVariables.colors.primitive.black,
                            }}
                        />
                    )}

                    <div className={css(S.ArticleInner)}>
                        <div className={css(S.FilterTogglesSection)}>
                            <SearchFilter
                                searchFilters={searchFilters}
                                filterDrawerOpen={filterDrawerOpen}
                                toggleFilterDrawer={toggleFilterDrawer}
                                disableSort={disableSort}
                            />
                        </div>
                    </div>
                    {searchFilters.appliedFilters &&
                        searchFilters.appliedFilters.length > 0 && (
                            <AppliedFilters
                                filterItems={searchFilters.appliedFilters}
                                resetFilters={() =>
                                    searchFilters.resetFilters(
                                        resetFilterStates(
                                            searchFilters.filterStates,
                                            ignoreFilterKeys,
                                        ),
                                    )
                                }
                            />
                        )}

                    {articles.length < 1 ? (
                        <NoSearchResults resultsType={'articles'} />
                    ) : (
                        <Grid
                            columns={
                                mobile
                                    ? 1
                                    : `repeat(5, ${pxToRem(
                                          S.TILE_WIDTH_DESKTOP,
                                      )})`
                            }
                            className={css(
                                mobile
                                    ? S.MobileArticleTile
                                    : S.DesktopArticleTile,
                            )}
                        >
                            <ArticleTiles
                                articles={articles}
                                appDisplayMode={appDisplayMode}
                                tracker={tracker}
                                disableTileLink={disableTileLink}
                                disableSaveToDesignBoard={
                                    disableSaveToDesignBoard
                                }
                                isBMPreview={isBMPreview}
                                getTileTopActions={getTileTopActions}
                                isProfessionalProfile={isProfessionalProfile}
                            />
                        </Grid>
                    )}

                    <BottomPagination
                        {...{
                            totalCount,
                            totalPage,
                            currentPage,
                            pageSize,
                            hasPreviousPage,
                            hasNextPage,
                        }}
                        itemName="Articles"
                        appDisplayMode={{
                            mobile: mobile,
                            desktop: !mobile,
                        }}
                        wrapperClassName={
                            mobile ? undefined : css(S.DesktopPaginationWrapper)
                        }
                        onPageChange={(page: number) => {
                            tracker.log('ProfessionalArticlePageChange', {
                                url: new URL(window.location.href),
                                targetTracker: 'archiproTracker',
                                data: {
                                    ExtraData: page,
                                },
                            });
                        }}
                    />
                </div>

                {hotButtonModal}
            </div>
        </Container>
    );
};

export default PaginatedJournalArticles;
